import React from 'react';
import { Calendar, MapPin, Clock, Banknote, Briefcase, CheckCircle, X, RefreshCw, AlertCircle, User, Shield, Wrench, Car } from 'lucide-react';

const STATUS_STYLES = {
  SENT: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', label: 'Pending' },
  COUNTERED: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'Counter Offer' },
  ACCEPTED: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', label: 'Accepted' },
  DECLINED: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: 'Declined' },
  EXPIRED: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-500', label: 'Expired' },
  CANCELLED: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-500', label: 'Cancelled' }
};

const EXPERIENCE_LABELS = {
  IMPROVER: 'Improver',
  SKILLED: 'Skilled Tradesperson',
  SUPERVISOR: 'Supervisor/Lead'
};

const PAYMENT_LABELS = {
  SAME_DAY: 'Same Day',
  WEEKLY: 'Weekly',
  '14_DAYS': '14 Days',
  '30_DAYS': '30 Days'
};

export default function OfferCard({ 
  offer, 
  currentUserId, 
  currentUserType,
  onAccept, 
  onCounter, 
  onDecline,
  onCancel,
  compact = false 
}) {
  if (!offer) return null;

  const offerData = offer.counter_offer || offer.offer;
  const status = offer.status;
  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.SENT;
  
  const isEmployer = currentUserType === 'employer';
  const isTradesperson = currentUserType === 'tradesperson';
  const isMyOffer = (isEmployer && offer.employer_id === currentUserId) || 
                   (isTradesperson && offer.countered_by === currentUserId);
  
  const canAccept = (status === 'SENT' && isTradesperson) || 
                   (status === 'COUNTERED' && isEmployer && offer.countered_by !== currentUserId);
  const canCounter = (status === 'SENT' && isTradesperson) || 
                    (status === 'COUNTERED' && !isMyOffer);
  const canDecline = status === 'SENT' || status === 'COUNTERED';
  const canCancel = isEmployer && (status === 'SENT' || status === 'COUNTERED');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr;
  };

  // Calculate time until expiry
  const getExpiryText = () => {
    if (!offer.expires_at || status === 'ACCEPTED' || status === 'DECLINED') return null;
    const expires = new Date(offer.expires_at);
    const now = new Date();
    const hoursLeft = Math.max(0, Math.floor((expires - now) / (1000 * 60 * 60)));
    if (hoursLeft <= 0) return 'Expired';
    if (hoursLeft < 24) return `${hoursLeft}h left to respond`;
    return `${Math.floor(hoursLeft / 24)}d left`;
  };

  const expiryText = getExpiryText();

  return (
    <div 
      className={`rounded-xl border-2 ${statusStyle.border} ${statusStyle.bg} overflow-hidden`}
      data-testid={`offer-card-${offer.offer_id}`}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 bg-white/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase className={`h-4 w-4 ${statusStyle.text}`} />
          <span className={`text-sm font-semibold ${statusStyle.text}`}>
            {statusStyle.label}
          </span>
          {offer.counter_count > 0 && (
            <span className="text-xs text-slate-500">(Counter #{offer.counter_count})</span>
          )}
        </div>
        {expiryText && status !== 'ACCEPTED' && status !== 'DECLINED' && (
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {expiryText}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Dates */}
        <div className="flex items-start gap-3">
          <Calendar className="h-5 w-5 text-orange-500 mt-0.5" />
          <div>
            <p className="font-medium text-slate-900">
              {formatDate(offerData.startDate)}
              {!offerData.singleDay && offerData.endDate && (
                <> - {formatDate(offerData.endDate)}</>
              )}
            </p>
            <p className="text-sm text-slate-500">
              {offerData.hoursStart} - {offerData.hoursEnd}
              {offerData.breaks && offerData.breaks !== 'NA' && (
                <span className="ml-2">• {offerData.breaks.toLowerCase()} breaks</span>
              )}
            </p>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-orange-500 mt-0.5" />
          <div>
            <p className="font-medium text-slate-900">
              {offerData.locationPostcode}
              {offerData.locationTown && `, ${offerData.locationTown}`}
            </p>
            {offerData.locationAddressLine1 && offerData.locationAddressLine1 !== '[Hidden until accepted]' ? (
              <p className="text-sm text-slate-500">{offerData.locationAddressLine1}</p>
            ) : (
              <p className="text-sm text-amber-600 italic">Full address shown after acceptance</p>
            )}
          </div>
        </div>

        {/* Pay */}
        <div className="flex items-start gap-3">
          <Banknote className="h-5 w-5 text-orange-500 mt-0.5" />
          <div>
            {offerData.rateType === 'DAY' && offerData.dayRateGBP ? (
              <p className="font-medium text-slate-900">£{offerData.dayRateGBP}/day</p>
            ) : (
              <p className="font-medium text-slate-900">Open to offers</p>
            )}
            {offerData.extras?.paymentTerms && (
              <p className="text-sm text-slate-500">
                Payment: {PAYMENT_LABELS[offerData.extras.paymentTerms] || offerData.extras.paymentTerms}
              </p>
            )}
          </div>
        </div>

        {/* Experience Level */}
        {offerData.experienceLevel && (
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-orange-500" />
            <p className="text-slate-700">{EXPERIENCE_LABELS[offerData.experienceLevel]}</p>
          </div>
        )}

        {/* Tasks Note */}
        {offerData.tasksNote && (
          <div className="bg-white/60 rounded-lg p-3 mt-2">
            <p className="text-sm text-slate-700">{offerData.tasksNote}</p>
          </div>
        )}

        {/* Counter Note */}
        {offerData.counterNote && status === 'COUNTERED' && (
          <div className="bg-amber-100/50 rounded-lg p-3 mt-2 border border-amber-200">
            <p className="text-xs font-medium text-amber-700 mb-1">Counter Note:</p>
            <p className="text-sm text-amber-800">{offerData.counterNote}</p>
          </div>
        )}

        {/* Extras Chips */}
        {offerData.extras && !compact && (
          <div className="flex flex-wrap gap-2 pt-2">
            {offerData.extras.cscsRequired && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                <Shield className="h-3 w-3" />
                CSCS {offerData.extras.cscsLevel || 'Required'}
              </span>
            )}
            {offerData.extras.tools && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                <Wrench className="h-3 w-3" />
                Tools: {offerData.extras.tools.toLowerCase()}
              </span>
            )}
            {offerData.extras.parkingAvailable && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                <Car className="h-3 w-3" />
                Parking
              </span>
            )}
            {offerData.extras.cisAvailable && (
              <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">CIS</span>
            )}
            {offerData.extras.inductionRequired && (
              <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">Induction</span>
            )}
            {offerData.crewSize > 1 && (
              <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">
                Crew: {offerData.crewSize}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      {(canAccept || canCounter || canDecline || canCancel) && (
        <div className="px-4 py-3 bg-white border-t border-slate-200 flex flex-wrap gap-2">
          {canAccept && (
            <button
              onClick={() => onAccept?.(offer.offer_id)}
              className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-1"
              data-testid="accept-offer-btn"
            >
              <CheckCircle className="h-4 w-4" />
              Accept
            </button>
          )}
          {canCounter && (
            <button
              onClick={() => onCounter?.(offer)}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-medium text-sm py-2 px-4 rounded-lg flex items-center justify-center gap-1"
              data-testid="counter-offer-btn"
            >
              <RefreshCw className="h-4 w-4" />
              Counter
            </button>
          )}
          {canDecline && (
            <button
              onClick={() => onDecline?.(offer.offer_id)}
              className="btn-secondary text-sm py-2 flex items-center justify-center gap-1"
              data-testid="decline-offer-btn"
            >
              <X className="h-4 w-4" />
              Decline
            </button>
          )}
          {canCancel && !canAccept && (
            <button
              onClick={() => onCancel?.(offer.offer_id)}
              className="text-slate-500 hover:text-slate-700 text-sm py-2 px-3"
              data-testid="cancel-offer-btn"
            >
              Cancel Offer
            </button>
          )}
        </div>
      )}

      {/* Accepted Badge */}
      {status === 'ACCEPTED' && (
        <div className="px-4 py-3 bg-green-100 border-t border-green-200 flex items-center justify-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="font-medium text-green-700">Job Confirmed!</span>
        </div>
      )}
    </div>
  );
}
